import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import Alert from "react-bootstrap/Alert";

import CircleInfoIcon from "../../../../styles/img/svg/CircleInfoIcon";
import CircleTickFill from "../../../../styles/img/svg/CircleTickFill";
import CircleFillX from "../../../../styles/img/svg/CircleFillX";

import { THOUGHTSPOT_CONFIG } from "../../../../_redux/_constants/insights.constant";

import {
    sherbotGetAIInstructionsRequest,
    sherbotGetAIInstructionError,
    sherbotUpdateAIInstructionRequest,
    sherbotUpdateAIInstructionComplete,
    sherbotUpdateAIInstructionError,
} from "../../../../_redux/_actions";

export default function AiInstructionForm() {
    const worksheetId = THOUGHTSPOT_CONFIG.THOUGHTSPOTTER_WORKSHEET_ID;

    const maxAIInstructionChars = 2500;

    const [aiInstruction, setAIInstruction] = useState("");

    const {
        sherbotAIInstructionResult,
        sherbotAIInstructionLoading,
        sherbotAIInstructionError,
    } = useSelector((state) => {
        return {
            sherbotAIInstructionResult:
                state.insights.sherbotAIInstructionResult?.nl_instructions_info[0].instructions?.join(
                    "\n",
                ),
            sherbotAIInstructionLoading:
                state.insights?.sherbotAIInstructionLoading,
            sherbotAIInstructionError:
                state.insights?.sherbotAIInstructionError,
        };
    });

    const {
        isInstructionUpdateSuccess,
        sherbotAIInstructionUpdateLoading,
        sherbotAIInstructionUpdateError,
    } = useSelector((state) => {
        return {
            isInstructionUpdateSuccess:
                state.insights.sherbotAIInstructionUpdateResult?.success,
            sherbotAIInstructionUpdateLoading:
                state.insights.sherbotAIInstructionUpdateLoading,
            sherbotAIInstructionUpdateError:
                state.insights.sherbotAIInstructionUpdateError,
        };
    });

    const actionDispatch = useDispatch();

    useEffect(() => {
        actionDispatch(sherbotGetAIInstructionsRequest(worksheetId));

        return () => {
            clearAIInstructionFetchError();
            clearAIInstructionUpdateState();
            clearAIInstructionUpdateError();
        };
    }, []);

    useEffect(() => {
        setAIInstruction(sherbotAIInstructionResult);
    }, [sherbotAIInstructionResult]);

    const handleInstructionSubmit = (e) => {
        e.preventDefault();

        // Here you can convert to array and send via fetch/curl

        const aiInstructionsArray = aiInstruction
            .split("\n") // split by new line
            .map((line) => line.trim()) // remove extra spaces
            .filter((line) => line !== ""); // remove empty lines

        const payload = {
            worksheetId: worksheetId,
            aiInstruction: aiInstructionsArray,
        };

        actionDispatch(sherbotUpdateAIInstructionRequest(payload));
    };

    const LoadingSpinner = (props) => {
        return (
            <div className="sb-spinner sb-flex-col sb-justify-center sb-items-center">
                <div className="text-center">
                    <div className="spinner-border" role="status"></div>
                </div>
                {props.text || "Loading..."}
            </div>
        );
    };

    const clearAIInstructionFetchError = () => {
        actionDispatch(
            sherbotGetAIInstructionError({
                error: null,
            }),
        );
    };

    const clearAIInstructionUpdateState = () => {
        actionDispatch(sherbotUpdateAIInstructionComplete(null));
    };

    const clearAIInstructionUpdateError = () => {
        actionDispatch(
            sherbotUpdateAIInstructionError({
                error: null,
            }),
        );
    };

    const StatusAlert = ({ type = "info", message, onClose }) => {
        const getIcon = (type) => {
            switch (type) {
                case "success":
                    return <CircleTickFill />;
                case "danger":
                    return <CircleFillX />;
                default:
                    return <CircleInfoIcon />;
            }
        };

        return (
            <Alert variant={type} dismissible={!!onClose} onClose={onClose}>
                <div className="sb-flex-row sb-gap-1">
                    <div>{getIcon(type)}</div>

                    <p className="sb-b-0">{message}</p>
                </div>
            </Alert>
        );
    };

    return (
        <div>
            {sherbotAIInstructionLoading && (
                <LoadingSpinner text={"Loading..."} />
            )}

            {sherbotAIInstructionUpdateLoading && (
                <LoadingSpinner text={"Saving..."} />
            )}

            {isInstructionUpdateSuccess && (
                <StatusAlert
                    type={"success"}
                    onClose={clearAIInstructionUpdateState}
                    message={"Sherbot AI Instructions updated successfully"}
                />
            )}

            {[
                {
                    errorMessage: sherbotAIInstructionError,
                    onClear: () => clearAIInstructionFetchError,
                },
                {
                    errorMessage: sherbotAIInstructionUpdateError,
                    onClear: () => clearAIInstructionUpdateError,
                },
            ].map((error, index) => {
                if (!error.errorMessage) return;

                return (
                    <StatusAlert
                        key={index}
                        type={"danger"}
                        onClose={error.onClear()}
                        message={error.errorMessage}
                    />
                );
            })}

            <form onSubmit={handleInstructionSubmit}>
                <div className="sb-flex-col w-100 sb-gap-1">
                    <Alert variant="info" className="sb-mb-0">
                        <div className="sb-flex-row sb-gap-1">
                            <div>
                                <CircleInfoIcon />
                            </div>
                            <p className="sb-b-0">
                                These instructions guide Sherbot AI on how to
                                understand and respond to user queries across
                                your chatbot. Use this box to provide global
                                context, rules, and guidance that will apply
                                globally.
                            </p>
                        </div>
                    </Alert>

                    <textarea
                        className="w-100 sb-p-1"
                        rows="11"
                        style={{
                            maxHeight: "20rem", // maximum height in px
                            overflowY: "auto", // show scrollbar if content exceeds max height
                        }}
                        placeholder="Enter NL instructions here..."
                        maxLength={maxAIInstructionChars}
                        value={aiInstruction}
                        onChange={(e) => setAIInstruction(e.target.value)}
                    />

                    {/* Character count display */}
                    <div
                        style={{
                            fontSize: "0.9rem",
                            color: "#555",
                        }}
                    >
                        {aiInstruction?.length} / {maxAIInstructionChars}{" "}
                        characters
                    </div>

                    <div className="sb-flex-row sb-justify-end">
                        <Button className="sb-primary-btn--38" type="submit">
                            Save
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
